import {gsap} from "../../../libs/gsap/all.js"
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameCActionButtons} from "./components/GameCActionButtons";
import {LanguageSwitcher} from "./components/LanguageSwitcher";
import {TextIntro} from "./components/TextIntro";
import {TextPopup} from "./components/TextPopup";
import {Bubble} from "./components/Bubble";
import {Cloud} from "./components/Cloud";
import {FadeUpSprite} from "./components/FadeUpSprite";
import {Earth} from "./components/Earth";
import {Bird} from "./components/Bird";
import {Tree} from "./components/Tree";
import {GameCMainCharacter} from "./components/GameCMainCharacter";
import {nanoid} from "nanoid";
import {GameCWalker} from "./components/GameCWalker";
import {Howl} from "howler";

export class GameC extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);

    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;

    this.mainTimeline = false;
    this.progression = 0;
    this.gameCompleted = false;
    this.gameStarted = false;
    this.currentScreen = 0;

    this.isWalking = false;
    this.globalAccelerationDuration = 1;
    this.globalAccelerationEasing = "power1.inOut";
    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.previousTargetSpeed = 0
    this.targetSpeedLerp = false;

    this.noProgressTimer = false;
    this.lastProgessTick = 0;
    this.lastSpawnTick = 0;

    this.vehicules = {
      walkers: {},
    };

    this.maxX = 0; // this will be set by the background layer size
    this.baseSpeed = 10; // pixels to move each tick when pressed

    this.layersSpeed = {
      "backgroundLayer": 1, // keep this to 1 always as this is the "base" speed
      "cloudsBackgroundLayer": 1.25,
      "photosLayer": 1.025,
      "photosCreditsLayer": 1.025,
      "buildingsLayer": 1.05,
      "treesLayer": 1.1,
      "floorLayer": 1,
      "textLayer": 1,
      "textIntroLayer": 1,
      "bubblesBackgroundLayer": 1.15,
      "bubblesLayer": 1.15,
      "birdLayer": 0.95,
      "foregroundTreesLayer": 1.5,
      "foregroundBubblesLayer": 1.5,
      "foregroundCloudsLayer": 1.25,
      "roadBackgroundTreesLayer": 1.15,
      "roadForegroundTreesLayer": 1.20,
    };

    this.walkSoundId = null;
    this.walkSounds = [new Howl({
      src: ["audio/MAF_Interaction 3 - HARD.oga"],
      loop: true,
      autoplay: true,
      volume: 0,
    }), new Howl({
      src: ["audio/MAF_Interaction 3 - SOFT.oga"],
      loop: true,
      autoplay: true,
      volume: 0,
    })];

    this.completeSound = new Howl({
      src: ["audio/MAF_Themes_Ahhh.oga"],
      loop: false,
      autoplay: false,
      volume: 1,
    });
  }

  async assetLoader() {
    // Game related assets
    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }

    // Global assets
    if (app.data.global.medias && app.data.global.medias.length) {
      for (let i = 0; i < app.data.global.medias.length; i++) {
        this.assets[app.data.global.medias[i].name] = await PIXI.Assets.load(app.data.global.medias[i].url);
      }
    }
  }

  async buildStage() {
    this.stage.eventMode = "static";
    this.stage.hitArea = this.screen;

    this.buildBackground();
    this.buildOutBackground();
    this.buildCloudsBackgroundLayer();
    this.buildPhotosLayer();
    this.buildBuildingsLayer();
    this.buildBubblesBackgroundLayer();
    this.buildTreesLayer();
    this.buildFloorLayer();

    this.buildBubblesLayer();
    this.buildBirdsLayer();

    this.buildVehiculesLayer();

    this.buildRoadBackgroundTrees();
    this.buildRoadBackgroundPedestrians();
    this.buildRoadForegroundTrees();
    this.buildRoadForegroundPedestrians();

    this.buildCharacterLayer();

    this.buildForegroundCloudsLayer();

    this.buildTextIntroLayer();
    this.buildTextPopupLayer();
    this.buildPhotoCreditsLayer();

    this.buildSoundscape();

    this.buildForegroundTreesLayer();
    this.buildForegroundBubblesLayer();

    this.buildAppTitle();
    this.buildUI();
    this.buildOverlay();

    this.buildMaintimeline();
    this.buildOutTimeline();

    //add listener
    this.ticker.add(this.progress.bind(this));
  }

  buildRoadBackgroundTrees() {
    this.roadBackgroundTreesLayer = new PIXI.Container();
    this.roadBackgroundTreesLayer.eventMode = 'none';
    this.stage.addChild(this.roadBackgroundTreesLayer);

    const trees = [
      [7950 * this.layersSpeed["roadBackgroundTreesLayer"], 1374, PIXI.Texture.from("road-tree-1"), 1, this],
      [10175 * this.layersSpeed["roadBackgroundTreesLayer"], 1420, PIXI.Texture.from("road-tree-3"), 1, this],
      [12700 * this.layersSpeed["roadBackgroundTreesLayer"], 1088, PIXI.Texture.from("road-tree-5"), 1, this],
      // [13900 * this.layersSpeed["roadBackgroundTreesLayer"], 1246, PIXI.Texture.from("road-tree-6"), 1, this],
      [15700 * this.layersSpeed["roadBackgroundTreesLayer"], 1088, PIXI.Texture.from("road-tree-8"), 1, this],
      [16300 * this.layersSpeed["roadBackgroundTreesLayer"], 1234, PIXI.Texture.from("road-tree-9"), 1, this],
    ];
    for (let i = 0; i < trees.length; i++) {
      const tree = new Tree(...trees[i]);
      this.roadBackgroundTreesLayer.addChild(tree);
    }

    // Deer
    const deer = new PIXI.Sprite(this.assets["deer"]);
    deer.position.set(16525, 1530);
    this.roadBackgroundTreesLayer.addChild(deer);
  }

  buildRoadBackgroundPedestrians() {
    this.roadBackgroundPedestriansLayer = new PIXI.Container();
    this.roadBackgroundPedestriansLayer.eventMode = 'none';
    this.stage.addChild(this.roadBackgroundPedestriansLayer);

    // TODO: Generate? walkers using pedestrians spritesheet
  }

  buildRoadForegroundTrees() {
    this.roadForegroundTreesLayer = new PIXI.Container();
    this.roadForegroundTreesLayer.eventMode = 'none';
    this.stage.addChild(this.roadForegroundTreesLayer);

    const trees = [
      [9700 * this.layersSpeed["roadForegroundTreesLayer"], 1502, PIXI.Texture.from("road-tree-2"), 1, this],
      [11100 * this.layersSpeed["roadForegroundTreesLayer"], 1340, PIXI.Texture.from("road-tree-4"), 1, this],
      [15200 * this.layersSpeed["roadForegroundTreesLayer"], 830, PIXI.Texture.from("road-tree-7"), 1, this],
      [16800 * this.layersSpeed["roadForegroundTreesLayer"], 1206, PIXI.Texture.from("road-tree-5"), 1, this],
    ];
    for (let i = 0; i < trees.length; i++) {
      const tree = new Tree(...trees[i]);
      this.roadForegroundTreesLayer.addChild(tree);
    }
  }

  buildRoadForegroundPedestrians() {
    this.roadForegroundPedestriansLayer = new PIXI.Container();
    this.roadForegroundPedestriansLayer.eventMode = 'none';
    this.stage.addChild(this.roadForegroundPedestriansLayer);

    // TODO: Generate? walkers using pedestrians spritesheet
  }

  buildBackground() {

    this.backgroundLayer = new PIXI.Container();
    this.backgroundLayer.eventMode = 'none';
    this.stage.addChild(this.backgroundLayer);

    let newBackgroundPosition = 0;
    for (let i = 1; i <= 8; i++) {
      let bgPart = new PIXI.Sprite(this.assets["background-" + i]);
      this.backgroundLayer.addChild(bgPart);

      bgPart.position.x = newBackgroundPosition;
      newBackgroundPosition = newBackgroundPosition + bgPart.width;
    }

    this.maxX = this.backgroundLayer.width;
  }

  buildOutBackground() {

    this.outBackgroundLayer = new PIXI.Container();
    this.outBackgroundLayer.eventMode = 'none';
    this.stage.addChild(this.outBackgroundLayer);

    for (let i = 1; i <= 2; i++) {
      let bgPart = new PIXI.Sprite(this.assets["out-background-" + i]);
      this.outBackgroundLayer.addChild(bgPart);
      bgPart.position.x = bgPart.width * (i - 1);
    }

    this.outBackgroundLayer.position.x = this.appWidth;
  }

  buildFloorLayer() {

    this.floorLayer = new PIXI.Container();
    this.floorLayer.eventMode = 'none';
    this.stage.addChild(this.floorLayer);

    let newFloorPosition = 0;
    for (let i = 1; i <= 8; i++) {
      let floorPart = new PIXI.Sprite(this.assets["floor-" + i]);
      this.floorLayer.addChild(floorPart);

      floorPart.position.x = newFloorPosition;
      newFloorPosition = newFloorPosition + floorPart.width;
    }
  }

  buildCloudsBackgroundLayer() {
    this.cloudsBackgroundLayer = new PIXI.Container();
    this.cloudsBackgroundLayer.eventMode = "none";
    this.stage.addChild(this.cloudsBackgroundLayer);

    this.clouds = [];
    const clouds = [
      [256, 1036, PIXI.Texture.from("cloud-12"), 1, this],
      [3300, 694, PIXI.Texture.from("cloud-14"), 1, this]
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.cloudsBackgroundLayer.addChild(cloud);
    }
  }

  buildBirdsLayer() {

    this.birdLayer = new PIXI.Container();
    this.birdLayer.eventMode = "none";
    this.stage.addChild(this.birdLayer);

    this.birds = [];
    const birds = [
      [[4600 * this.layersSpeed["birdLayer"], 832, PIXI.Texture.from("bird-2"), 0.95, this]],
      [[6575 * this.layersSpeed["birdLayer"], 264, PIXI.Texture.from("bird-3"), 0.95, this]],
      [[10425 * this.layersSpeed["birdLayer"], 352, PIXI.Texture.from("bird-4"), 0.95, this]],
      [[10675 * this.layersSpeed["birdLayer"], 992, PIXI.Texture.from("bird-6"), 0.95, this]],
      [[11250 * this.layersSpeed["birdLayer"], 626, PIXI.Texture.from("bird-5"), 0.95, this]],
      [[11613 * this.layersSpeed["birdLayer"], 200, PIXI.Texture.from("bird-7"), 0.95, this]],
      [[12225 * this.layersSpeed["birdLayer"], 978, PIXI.Texture.from("bird-8"), 0.95, this]],
      [[12325 * this.layersSpeed["birdLayer"], 682, PIXI.Texture.from("bird-9"), 0.95, this]],
      [[14075 * this.layersSpeed["birdLayer"] - 1500, 868, PIXI.Texture.from("bird-1"), 0.95, this]],
      [[14475 * this.layersSpeed["birdLayer"] - 1500, 666, PIXI.Texture.from("bird-10"), 0.95, this]],
      [[14725 * this.layersSpeed["birdLayer"] - 1500, 706, PIXI.Texture.from("bird-11"), 0.95, this]],
      [[14885 * this.layersSpeed["birdLayer"] - 1500, 464, PIXI.Texture.from("bird-12"), 0.95, this]],
      [[15075 * this.layersSpeed["birdLayer"], 121, PIXI.Texture.from("bird-11"), 0.95, this]],
      [[15675 * this.layersSpeed["birdLayer"], 714, PIXI.Texture.from("bird-13"), 0.95, this]],
      [[16405 * this.layersSpeed["birdLayer"], 63, PIXI.Texture.from("bird-14"), 0.95, this]],
      [[17285 * this.layersSpeed["birdLayer"], 897, PIXI.Texture.from("bird-17"), 0.95, this]],
      [[17775 * this.layersSpeed["birdLayer"], 996, PIXI.Texture.from("bird-2"), 0.95, this]],
      [[17925 * this.layersSpeed["birdLayer"], 694, PIXI.Texture.from("bird-15"), 0.95, this]],

      [[18390 * this.layersSpeed["birdLayer"], 975, PIXI.Texture.from("bird-10"), 0.99, this], 0.65],
      [[18470 * this.layersSpeed["birdLayer"], 917, PIXI.Texture.from("bird-9"), 0.99, this]],
      [[18490 * this.layersSpeed["birdLayer"], 1005, PIXI.Texture.from("bird-8"), 0.99, this]],
      [[18540 * this.layersSpeed["birdLayer"], 911, PIXI.Texture.from("bird-1"), 0.99, this], 0.55],
      [[18555 * this.layersSpeed["birdLayer"], 953, PIXI.Texture.from("bird-2"), 0.99, this], 0.75],
      [[18640 * this.layersSpeed["birdLayer"], 808, PIXI.Texture.from("bird-3"), 0.99, this], 0.475],
      [[18665 * this.layersSpeed["birdLayer"], 926, PIXI.Texture.from("bird-14"), 0.99, this], 0.38],
      [[18750 * this.layersSpeed["birdLayer"], 1004, PIXI.Texture.from("bird-10"), 0.99, this], 0.55],
      [[18805 * this.layersSpeed["birdLayer"], 895, PIXI.Texture.from("bird-4"), 0.99, this], 1.30],
      [[18875 * this.layersSpeed["birdLayer"], 828, PIXI.Texture.from("bird-11"), 0.99, this], 0.5],
      [[18930 * this.layersSpeed["birdLayer"], 918, PIXI.Texture.from("bird-10"), 0.99, this], 0.85],
      [[18940 * this.layersSpeed["birdLayer"], 845, PIXI.Texture.from("bird-13"), 0.99, this], 0.5],
      [[18960 * this.layersSpeed["birdLayer"], 704, PIXI.Texture.from("bird-12"), 0.99, this], 0.475],
      [[19010 * this.layersSpeed["birdLayer"], 826, PIXI.Texture.from("bird-7"), 0.99, this], 1.05],
      [[19090 * this.layersSpeed["birdLayer"], 816, PIXI.Texture.from("bird-8"), 0.99, this]],
    ];
    for (let i = 0; i < birds.length; i++) {
      const bird = new Bird(...birds[i][0]);

      // Apply scale
      if (birds[i][1] !== undefined) {
        bird.sprite.scale = birds[i][1];
      }

      this.birds.push(bird);
      this.birdLayer.addChild(bird);
    }
  }

  buildPhotosLayer() {
    this.photosLayer = new PIXI.Container();
    this.photosLayer.eventMode = 'none';
    this.stage.addChild(this.photosLayer);

    this.photos = [];

    this.photo1 = new FadeUpSprite(2753, 580, this.assets["photo-1"], 0.9, this);
    this.photosLayer.addChild(this.photo1);

    this.photo2 = new FadeUpSprite(6627 * this.layersSpeed["photosLayer"], 205, this.assets["photo-2"], 0.9, this);
    this.photosLayer.addChild(this.photo2);

    this.photo3 = new FadeUpSprite(11400 * this.layersSpeed["photosLayer"], 570, this.assets["photo-3"], 0.9, this);
    this.photosLayer.addChild(this.photo3);

    this.photos.push(this.photo1, this.photo2, this.photo3);

    // Let's add the credits layer here so we are at the right zIndex
    this.photosCreditsLayer = new PIXI.Container();
    this.stage.addChild(this.photosCreditsLayer);
  }

  buildBuildingsLayer() {
    this.buildingsLayer = new PIXI.Container();
    this.buildingsLayer.eventMode = 'none';

    let buildingPart1 = new PIXI.Sprite(this.assets["buildings-1"]);
    buildingPart1.position.x = 1208;
    buildingPart1.position.y = 1240;
    this.buildingsLayer.addChild(buildingPart1);

    let buildingPart2 = new PIXI.Sprite(this.assets["buildings-2"]);
    buildingPart2.position.x = 4000 * this.layersSpeed["buildingsLayer"];
    buildingPart2.position.y = 1204;
    this.buildingsLayer.addChild(buildingPart2);

    this.stage.addChild(this.buildingsLayer);
  }

  buildTreesLayer() {
    this.treesLayer = new PIXI.Container();
    this.treesLayer.eventMode = 'none';

    const treePart1 = new PIXI.Sprite(this.assets["trees-1"]);
    treePart1.position.set(672, 1167);
    this.treesLayer.addChild(treePart1);

    const treePart2 = new PIXI.Sprite(this.assets["trees-2"]);
    treePart2.position.set(9500 * this.layersSpeed["treesLayer"], 1145);
    this.treesLayer.addChild(treePart2);

    const treePart3 = new PIXI.Sprite(this.assets["trees-3"]);
    treePart3.position.set(13800 * this.layersSpeed["treesLayer"], 1108);
    this.treesLayer.addChild(treePart3);

    this.stage.addChild(this.treesLayer);
  }

  buildBubblesBackgroundLayer() {
    // Some bubbles are part of the background
    this.bubblesBackgroundLayer = new PIXI.Container();
    this.bubblesBackgroundLayer.eventMode = "none";
    this.stage.addChild(this.bubblesBackgroundLayer);

    this.bubbles = [];
    const bubbles = [
      [1014, 1242, 392, 0xFF6463, 1.0, this], // red
      [13100 * this.layersSpeed["bubblesBackgroundLayer"], 1422, 470, 0x7ABECC, 1.0, this], // blue
      [17600 * this.layersSpeed["bubblesBackgroundLayer"], 792, 596, 0xFF995A, 1.0, this], // orange
    ];
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.bubblesBackgroundLayer.addChild(bubble);
    }
  }

  buildTextIntroLayer() {
    // Containers
    this.textIntroLayer = new TextIntro(1, this.data.screens[0], this);
    this.textIntroLayer.eventMode = 'none';
    this.stage.addChild(this.textIntroLayer);
  }

  buildTextPopupLayer() {
    super.buildTextPopupLayer();

    const text1 = new TextPopup((this.appWidth * 1) + (this.appWidth * 0.43), 596, "#343B6F", 0.95, this.data.screens[1], this);
    this.textPopups.push(text1);
    if (text1.getText()) {
      // reduced position using text width
      text1.getText().position.x -= text1.getText().width / 2;
      this.textLayer.addChild(text1.getText());
    }
    if (text1.getPopup()) {
      this.popupLayer.addChild(text1.getPopup());
    }

    const text2 = new TextPopup((this.appWidth * 2) + (this.appWidth * 0.49), 596, "#343B6F", 0.95, this.data.screens[2], this);
    this.textPopups.push(text2);
    if (text2.getText()) {
      // reduced position using text width
      text2.getText().position.x -= text2.getText().width / 2;
      this.textLayer.addChild(text2.getText());
    }
    if (text2.getPopup()) {
      this.popupLayer.addChild(text2.getPopup());
    }

    const text4 = new TextPopup((this.appWidth * 3) + (this.appWidth * 0.75), 596, "#343B6F", 0.95, this.data.screens[4], this);
    this.textPopups.push(text4);
    if (text4.getText()) {
      // reduced position using text width
      text4.getText().position.x -= text4.getText().width / 2;
      this.textLayer.addChild(text4.getText());
    }
    if (text4.getPopup()) {
      this.popupLayer.addChild(text4.getPopup());
    }
  }

  buildBubblesLayer() {
    this.bubblesLayer = new PIXI.Container();
    this.bubblesLayer.eventMode = 'none';
    this.stage.addChild(this.bubblesLayer);

    const bubbles = [
      [-260, 770, 532, 0x869E80, 1.0, this], // dark green
      [2246, -510, 748, 0xFF6463, 1.0, this], // red
      [2678, 366, 178, 0xFF995A, 1.0, this], // orange
      [3184, 628, 68, 0xA4CEA2, 1.0, this], // light green
      [3625, 1036, 294, 0xFF6463, 0.95, this], // red
      [5900 * this.layersSpeed["bubblesLayer"], 898, 52, 0x7ABECC, 0.95, this], // blue
      [6225 * this.layersSpeed["bubblesLayer"], 772, 748, 0xFF6463, 0.95, this], // red
      [8000 * this.layersSpeed["bubblesLayer"], -650, 932, 0xFF995A, 0.95, this], // orange
      [8000 * this.layersSpeed["bubblesLayer"], 382, 52, 0x7ABECC, 0.95, this], // blue
      [10730 * this.layersSpeed["bubblesLayer"], 954, 110, 0x7ABECC, 0.95, this], // blue
      [10946 * this.layersSpeed["bubblesLayer"], 958, 52, 0x7ABECC, 0.95, this], // blue
      [11018 * this.layersSpeed["bubblesLayer"], 598, 364, 0x7ABECC, 0.95, this], // blue
      [11348 * this.layersSpeed["bubblesLayer"], -616, 748, 0x7ABECC, 0.95, this], // blue
      [12100 * this.layersSpeed["bubblesLayer"], 408, 86, 0xFF6463, 0.95, this], // red
      [13750 * this.layersSpeed["bubblesLayer"], -374, 596, 0x7ABECC, 0.95, this], // blue
      [14400 * this.layersSpeed["bubblesLayer"], 858, 146, 0x7ABECC, 0.95, this], // blue
      [14550 * this.layersSpeed["bubblesLayer"], 374, 320, 0x7ABECC, 0.95, this], // blue
      [17900 * this.layersSpeed["bubblesLayer"], 1566, 232, 0x7ABECC, 0.95, this], // blue
    ];
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.bubblesLayer.addChild(bubble);
    }

    // this.earth = new Earth(17400 * this.layersSpeed["bubblesLayer"], -184, this.assets["earth"], 1, this);
    // this.bubblesLayer.addChild(this.earth);
    this.earth = new Earth(this.appWidth - 1000, -187, this.assets["earth"], 1, this);
    this.stage.addChild(this.earth);
  }

  buildForegroundCloudsLayer() {
    this.foregroundCloudsLayer = new PIXI.Container();
    this.foregroundCloudsLayer.eventMode = "none";
    this.stage.addChild(this.foregroundCloudsLayer);

    const clouds = [
      [2550, 822, PIXI.Texture.from("cloud-13"), 1, this],
      [5800 * this.layersSpeed["foregroundCloudsLayer"], 822, PIXI.Texture.from("cloud-13"), 1, this],
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.foregroundCloudsLayer.addChild(cloud);
    }
  }

  buildForegroundTreesLayer() {
    this.foregroundTreesLayer = new PIXI.Container();
    this.foregroundTreesLayer.eventMode = 'none';
    this.stage.addChild(this.foregroundTreesLayer);

    // this.trees = [];
    const trees = [
      [11950 * this.layersSpeed["foregroundTreesLayer"], 938, this.assets["trees-foreground-1"], 1, this],
      [16550 * this.layersSpeed["foregroundTreesLayer"], 1082, this.assets["trees-foreground-2"], 1, this]
    ];
    for (let i = 0; i < trees.length; i++) {
      const tree = new Tree(...trees[i]);
      // this.trees.push(tree);
      this.foregroundTreesLayer.addChild(tree);
    }
  }

  buildForegroundBubblesLayer() {
    this.foregroundBubblesLayer = new PIXI.Container();
    this.foregroundBubblesLayer.eventMode = 'none';
    this.stage.addChild(this.foregroundBubblesLayer);

    const bubbles = [
      [2500, 2046, 380, 0x869E80, 0.95, this], // green
      [3800 * this.layersSpeed["foregroundBubblesLayer"], 1942, 1862, 0xA4CEA2, 0.95, this], // light green
      [8450 * this.layersSpeed["foregroundBubblesLayer"], 2078, 158, 0xFF6463, 0.95, this], // red
      [12300 * this.layersSpeed["foregroundBubblesLayer"], 1942, 1862, 0xA4CEA2, 0.95, this], // light green
    ];

    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.foregroundBubblesLayer.addChild(bubble);
    }
  }

  buildVehiculesLayer() {
    this.vehiculesLayer = new PIXI.Container();
    this.stage.addChild(this.vehiculesLayer);
  }

  buildCharacterLayer() {
    this.characterLayer = new PIXI.Container();
    this.stage.addChild(this.characterLayer);

    this.mainCharacter = new GameCMainCharacter(this);
    this.mainCharacter.x = 670;
    this.mainCharacter.y = 1930;
    this.characterLayer.addChild(this.mainCharacter);
  }

  spawnMissingVehicules(startsHidden, currentTime, type) {

    // if (type !== "cars") {
    let maxFPS = 6; // Limit spawning rate so they do not appear all at the same time
    let frameDuration = 1000 / maxFPS; // Calculate the duration of each frame in milliseconds

    if (currentTime - this.lastSpawnTick[type] < frameDuration) {
      // too soon, skip progress tick
      return;
    }
    // }

    this.lastSpawnTick[type] = currentTime;

    let vehicules = this.vehicules[type];

    const vehiculeOfTypeToAdd = this.data.screens[this.currentScreen]["max_" + type] - Object.keys(vehicules).length;

    if (vehiculeOfTypeToAdd > 0) {
      // console.log("Need to add ", vehiculeOfTypeToAdd, type);
      const randomVehiculeID = nanoid(); // we'll use a random unique id so we can reference the vehicule easily later on

      let vehicule = null;

      if (type == "walkers") {
        vehicule = new GameCWalker(randomVehiculeID, 0, this, startsHidden);
      }

      if (vehicule) {
        this.vehicules[type][randomVehiculeID] = vehicule;
        this.vehiculesLayer.addChild(vehicule);
      }

    }

  }

  spawnAllMissingVehiculesOnScreen() {

    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      const vehiculeOfTypeToAdd = this.data.screens[this.currentScreen]["max_" + type] - Object.keys(vehicules).length;

      if (vehiculeOfTypeToAdd > 0) {
        // console.log("Need to add ", vehiculeOfTypeToAdd, type);
        for (let i = 0; i < vehiculeOfTypeToAdd; i++) {
          const randomVehiculeID = nanoid(); // we'll use a random unique id so we can reference the vehicule easily later on

          let vehicule = null;

          if (type == "walkers") {
            vehicule = new GameCWalker(randomVehiculeID, i, this, false);
          }

          if (vehicule) {
            this.vehicules[type][randomVehiculeID] = vehicule;
            this.vehiculesLayer.addChild(vehicule);
          }
        }
      }
    }
  }

  checkVehiculesCount(currentTime, type) {
    // for (const [type, vehicules] of Object.entries(this.vehicules)) {
    let totalActiveVehicules = 0;
    for (const [id, vehicule] of Object.entries(this.vehicules[type])) {
      if (vehicule.taggedForDestroy === false) {
        totalActiveVehicules++;
      }
    }

    const vehiculeOfTypeToRemove = Math.max(0, totalActiveVehicules - this.data.screens[this.currentScreen]["max_" + type]);
    if (vehiculeOfTypeToRemove > 0) {
      // console.log("Need to remove ", vehiculeOfTypeToRemove, type);

      let randomVehiculesIDsToRemove = Object.keys(this.vehicules[type]).sort((a, b) => 0.5 - Math.random()); // randomize array of keys

      randomVehiculesIDsToRemove = randomVehiculesIDsToRemove.slice(0, vehiculeOfTypeToRemove); // select only vehiculeOfTypeToRemove elements

      for (let i = 0; i < randomVehiculesIDsToRemove.length; i++) {
        this.vehicules[type][randomVehiculesIDsToRemove[i]].disable();
        this.vehicules[type][randomVehiculesIDsToRemove[i]].on("disabled", this.onVehiculeDisabled.bind(this));
      }

    }

  }

  removeAllVehicules() {
    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      for (const [id, vehicule] of Object.entries(this.vehicules[type])) {
        this.onVehiculeDisabled({type: type, id: id});
      }

    }
  }

  onVehiculeDisabled(vehicule) {
    this.vehicules[vehicule.type][vehicule.id].off("disabled", this.onVehiculeDisabled.bind(this));
    this.vehicules[vehicule.type][vehicule.id].destroy();
    delete this.vehicules[vehicule.type][vehicule.id];
  }

  buildUI() {

    this.uiLayer = new PIXI.Container();
    this.stage.addChild(this.uiLayer);

    // TODO: Remove, this is only used for testing timeline
    // TODO: Use finger walk instead of crankset for movement
    this.actionButtons = new GameCActionButtons(this);
    this.actionButtons.x = 3140;
    this.actionButtons.y = 1552;
    this.uiLayer.addChild(this.actionButtons);
    //
    // this.actionButtons.on("take-a-step", this.onTakeAStep, this);
    this.actionButtons.on("walk", this.onWalk, this);
    this.actionButtons.on("stop-walk", this.onStopWalk, this);

    // Add language switcher
    this.languageSwitcher = new LanguageSwitcher(this);
    this.stage.addChild(this.languageSwitcher);
  }

  buildMaintimeline() {
    let maxX = -this.backgroundLayer.width + this.appWidth;

    this.mainTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 30,
        ease: "none"
      }
    });

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.mainTimeline.to(this[name], {
        pixi: {
          x: maxX * speed,
        }
      }, 0);
    }

    // Slide down the earth
    this.mainTimeline.fromTo(this.earth, {
      pixi: {
        y: -2000,
      },
    }, {
      pixi: {
        y: -187,
      },
      immediateRender: true,
      ease: "power1.out",
      duration: 8,
    }, "-=8");
  }

  buildOutTimeline() {
    this.outTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 4,
        ease: "linear",
      },
      onComplete: () => {
        this.dispatchEvent(new CustomEvent("complete", {detail: "GameC Completed."}))
      },
      onStart: () => {
        this.completeSound.seek(0).play();
      },
    });

    let slideDistance = 2000;

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.outTimeline.to(this[name], {
        pixi: {
          x: "-=" + (slideDistance * speed), // scroll 2/3 of the screen
        }
      }, 0);
    }

    this.outTimeline.to(this.outBackgroundLayer, {
      pixi: {
        x: "-=" + slideDistance, // arbitrary number that worked based on above transition
      }
    }, 0);

    this.outTimeline.fromTo(this.fadeOverlayLayer, {
      pixi: {
        alpha: 0,
      }
    }, {
      pixi: {
        alpha: 1,
      },
      duration: 2,
      overwrite: "auto"
    }, 2)
  }

  activate() {
    // Enable render
    this.start();
    this.ticker.start();
    this.soundscape.start();

    // TODO: start the timelines here so they are moving while fading in view
  }

  deactivate() {
    // Disable render
    this.stop();
    this.ticker.stop();

    this.disableUI();
    this.languageSwitcher.disable();

    // Stop sound
    this.walkSounds.forEach((walkSound) => walkSound.fade(walkSound.volume(), 0, 150));
    this.walkSoundId = null;

    this.soundscape.stopAll()

    // TODO: Pause all the timelines so the game isn't doing a thing while not on screen
  }

  begin() {
    // Game is ready to go, on screen, and animations have started

    this.enableUI();
    this.languageSwitcher.enable();

    this.fadeOutOverlay();
  }

  progress(ticker) {
    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      this.spawnMissingVehicules(false, ticker.lastTime, type);
    }

    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      this.checkVehiculesCount(ticker.lastTime, type);
    }

    this.moveVehicules(this.isWalking, ticker.deltaTime);

    if (this.gameCompleted) {
      return;
    }

    if (this.isWalking) {
      this.targetSpeed = this.baseSpeed;
    } else {
      this.targetSpeed = 0;
    }

    if (this.targetSpeed != this.previousTargetSpeed) {
      if (this.targetSpeed > 0) {
        this.mainCharacter.startWalking();
      }

      if (this.targetSpeedLerp !== false) {
        this.targetSpeedLerp.kill();
        this.targetSpeedLerp = false;
      }

      this.targetSpeedLerp = gsap.to(this,
        {
          currentSpeed: this.targetSpeed,
          duration: this.globalAccelerationDuration,
          ease: this.globalAccelerationEasing,
          overwrite: "auto",
          onComplete: () => {
            if (this.targetSpeed === 0) {
              this.mainCharacter.stopWalking();
            }
          }
        }
      );

      this.previousTargetSpeed = this.targetSpeed;
    }


    // Will technically only progress if speed is greater than 0
    this.progression += ((this.currentSpeed / this.maxX) * ticker.deltaTime);
    this.progression = Math.min(this.progression, 1);

    this.mainTimeline.progress(this.progression);

    this.currentScreen = Math.min(this.data.screens.length - 1, Math.floor(this.data.screens.length * this.progression));

    if (this.mainTimeline.progress() >= 1) {
      // reached the end, let's transition to the end and complete
      // transition out and dispatch complete event
      // console.log("game completed");

      // force walk animation
      this.isWalking = true;
      this.mainCharacter.startWalking();

      this.gameCompleted = true;
      this.disableUI();
      this.languageSwitcher.disable();
      for (let i = 0; i < this.textPopups.length; i++) {
        this.textPopups[i].disable();
      }

      if (this.targetSpeedLerp !== false) {
        this.targetSpeedLerp.kill();
        this.targetSpeedLerp = false;
      }

      this.outTimeline.play();

    }

    this.lastProgessTick = ticker.lastTime;
  }

  moveVehicules(backgroundMoving, deltaTime) {
    for (const [type, vehicules] of Object.entries(this.vehicules)) {
      for (const [id, vehicule] of Object.entries(vehicules)) {
        vehicule.move(backgroundMoving, deltaTime);
      }
    }
  }


  onWalk(data) {
    super.progress(this.mainTimeline.progress());

    // console.log("walk", data, this);
    if (this.gameCompleted) {
      return;
    }

    if (this.walkSoundId === null) this.walkSoundId = this.mainTimeline.progress() < 0.5 ? 0 : 1;
    this.walkSounds[this.walkSoundId].fade(this.walkSounds[this.walkSoundId].volume(), 1, 350)

    this.isWalking = true;
  }

  onStopWalk(e) {
    // console.log("walk", data, this);
    if (this.gameCompleted) {
      return;
    }

    this.walkSoundId = null;
    this.walkSounds.forEach((walkSound) => walkSound.fade(walkSound.volume(), 0, 350));

    this.isWalking = false;
  }

  enableUI() {
    // TODO: Modify code for finger walk
    this.actionButtons.enable();
  }

  disableUI() {
    // TODO: Modify code for finger walk
    this.actionButtons.disable();
  }

  // startNoActionTimer() {
  //   this.stopNoActionTimer();
  //
  //   this.noProgressTimer = setTimeout(() => {
  //     // TODO: set the right idle mode and not crankset
  //     this.actionButtons.startIdleMode();
  //   }, 5000)
  // }

  // stopNoActionTimer() {
  //   if (this.noProgressTimer !== false) {
  //     clearTimeout(this.noProgressTimer);
  //     this.noProgressTimer = false;
  //   }
  // }

  onTouchDown(e) {

    // this.mainCharacter.animateIn();
    // this.actionButtons.onPress();

    // TODO: stop the correct idle mode and not crankset
    // this.actionButtons.stopIdleMode();
    // this.stopNoActionTimer();

    // this.isWalking = true;

    // Intro, switch text when user start moving in timeline
    if (!this.gameStarted) {
      this.textIntroLayer.nextStep();
    }

    this.gameStarted = true;
  }

  onTouchUp(e) {
    // this.mainCharacter.animateOut();
    // this.actionButtons.onRelease();
    // this.isWalking = false;
  }

  reset() {
    this.gameStarted = false;
    this.gameCompleted = false;
    this.isWalking = false;

    this.currentScreen = 0;

    this.lastProgessTick = 0;
    this.lastSpawnTick = {
      walkers: 0
    };
    this.progression = 0;

    this.outTimeline.progress(0, true);
    this.mainTimeline.progress(0, true);

    this.outTimeline.kill();
    this.buildOutTimeline();

    this.currentSpeed = 0;
    this.targetSpeed = 0;
    this.previousTargetSpeed = 0;

    if (this.targetSpeedLerp !== false) {
      this.targetSpeedLerp.kill();
      this.targetSpeedLerp = false;
    }

    // this.stopNoActionTimer();

    // Reset intro
    this.textIntroLayer.resetSteps();
    this.textIntroLayer.reset();

    // Reset birds
    for (let i = 0; i < this.birds.length; i++) {
      this.birds[i].reset();
    }

    // Reset photos
    for (let i = 0; i < this.photos.length; i++) {
      this.photos[i].reset();
    }

    // Reset bubbles
    for (let i = 0; i < this.bubbles.length; i++) {
      this.bubbles[i].reset();
    }

    // Reset text popup
    for (let i = 0; i < this.textPopups.length; i++) {
      this.textPopups[i].hidePopup();
      this.textPopups[i].reset();
    }

    // Reset clouds
    for (let i = 0; i < this.clouds.length; i++) {
      this.clouds[i].reset();
    }

    // Reset vehicules
    this.removeAllVehicules();
    this.spawnAllMissingVehiculesOnScreen();

    // reset buttons
    this.mainCharacter.stopWalking();
    this.actionButtons.reset();

    this.onTouchUp();

    this.fadeOverlayLayer.alpha = 1;

  }
}
